import { useEffect, useMemo, useState } from 'react';
import { Controller } from 'react-hook-form';
import {
  Account,
  AccountsSelector,
  IconTrash,
  SchemaFilterType,
  SortDir,
  Table,
  useFilters,
} from '@ui/src';
import {
  columns,
  columnsLabels,
  filtersSchema,
} from '../../hoc/accounting-managment/variables/accounts';
import { AccountingJobItemAccountsTypes } from './types';

import cn from './styles.module.css';

const formatAccountData = (
  list: Account[],
  setDeleteAccountPopupVisible: (data: boolean) => void,
  setDeleteAccountId: (data: string) => void,
): Account[] => {
  const nextList = list?.map((item) => {
    const result = { ...item };

    const render: Record<string, React.JSX.Element> = {};

    render.search = (
      <div className={cn.accountDescriptionRow}>
        <div className={cn.accountDescription}>{item.description}</div>
        <button
          type="button"
          className={cn.accountDescriptionDeleteButton}
          onClick={() => {
            setDeleteAccountPopupVisible(true);
            setDeleteAccountId(result.id);
          }}
        >
          <IconTrash />
        </button>
      </div>
    );

    result.render = render;

    return result;
  });

  return nextList;
};

const Accounts = ({
  filters,
  control,
  onFilter,
  setPage,
  pagination,
  accounts,
  onSort,
  sort,
  accountsIds,
  isFetching,
  accountCategories,
  accountsCategoriesLoading,
  accountsIsLoading,
  onSearchAutocomplete,
  setAccountQuery,
  accountsList,
  setAccounts,
  setSelectedAccountsList,
  setValue,
  selectedAccountsList,
  errors,
  setDeleteAccountPopupVisible,
  setDeleteAllAccounts,
  setDeleteAccountId,
  newAccountsWasAdded,
  setNewAccountsWasAdded,
  isEditable,
  errorsList,
}: AccountingJobItemAccountsTypes) => {
  const [activeTab, setActiveTab] = useState<'saved' | 'new'>('saved');

  const { filtersComponent, handleSubmit } = useFilters({
    filtersSchema,
    filters,
    onFilterChange: onFilter,
  });

  const formattedData = useMemo(() => {
    const data = formatAccountData(
      accounts as Account[],
      setDeleteAccountPopupVisible,
      setDeleteAccountId,
    );

    return data;
  }, [accounts, filters]);

  useEffect(() => {
    if (newAccountsWasAdded) {
      setActiveTab('saved');
      setNewAccountsWasAdded(false);
    }
  }, [newAccountsWasAdded]);

  return (
    <div className={cn.accountsListWrapper}>
      <div className={cn.accountsListTabs}>
        {isEditable && (
          <>
            <button
              type="button"
              className={`${cn.accountsListTabButton} ${
                activeTab === 'saved' ? cn.active : ''
              }`}
              onClick={() => setActiveTab('saved')}
            >
              Initial added
            </button>
            <button
              type="button"
              className={`${cn.accountsListTabButton} ${
                activeTab === 'new' ? cn.active : ''
              }`}
              onClick={() => setActiveTab('new')}
            >
              New added
            </button>
          </>
        )}
      </div>
      {activeTab === 'new' && (
        <div className={cn.addAccountsWrapper}>
          <div className={cn.formGroup}>
            <AccountsSelector
              title="Account"
              placeholder="Enter name or number..."
              className={cn.selectDebit}
              accounts={accountCategories}
              isLoading={accountsCategoriesLoading}
              accountsIsLoading={accountsIsLoading}
              onSearch={(searchText) => onSearchAutocomplete(searchText)}
              setAccountQuery={setAccountQuery}
              accountsList={accountsList || []}
              setAccounts={setAccounts}
              setSelectedAccount={() => {}}
              setSelectedAccountsList={(data: Account[]) => {
                setSelectedAccountsList(data);

                setValue(
                  'accountsIds',
                  data?.map((item) => item.id),
                );
              }}
              selectedAccountsList={selectedAccountsList}
              fullWidth
              error={errors?.accountsIds?.message}
              multiple
            />
            <Controller
              control={control}
              name="accountsIds"
              render={({ field: { value, name } }) => (
                <input type="hidden" value={value} name={name} />
              )}
            />
          </div>
          {selectedAccountsList && selectedAccountsList?.length > 0 && (
            <div className={cn.selectedAccountsList}>
              <div className={cn.selectedAccountsListTop}>
                <div
                  className={cn.selectedAccountsListTopTitle}
                >{`${selectedAccountsList?.length} account${
                  selectedAccountsList?.length > 1 ? 's' : ''
                } added`}</div>
                <button
                  type="button"
                  className={cn.deletAllAccountsButton}
                  onClick={() => {
                    setValue('accountsIds', []);
                    setSelectedAccountsList([]);
                  }}
                >
                  Delete all
                </button>
              </div>

              {selectedAccountsList?.map((item) => (
                <div className={cn.selectedAccount} key={item.id}>
                  <div className={cn.selectedAccountNumber}>
                    {item.fullNumber}
                  </div>
                  <div className={cn.selectedAccountDescription}>
                    {item.description}
                  </div>
                  <button
                    type="button"
                    className={cn.deleteAccountButton}
                    onClick={() => {
                      const nextSelectedAccount = selectedAccountsList?.filter(
                        (account) => account.id !== item.id,
                      );
                      setSelectedAccountsList(nextSelectedAccount);
                      setValue(
                        'accountsIds',
                        nextSelectedAccount?.map((account) => account.id),
                      );
                    }}
                  >
                    <IconTrash />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {activeTab === 'saved' && (
        <>
          {accountsIds && accountsIds?.length > 0 && isEditable && (
            <div
              className={`${cn.selectedAccountsListTop} ${cn.withoutMargin}`}
            >
              <div
                className={cn.selectedAccountsListTopTitle}
              >{`${accountsIds?.length} account${
                accountsIds?.length > 1 ? 's' : ''
              } added`}</div>
              <button
                type="button"
                className={cn.deletAllAccountsButton}
                onClick={() => {
                  setDeleteAccountPopupVisible(true);
                  setDeleteAllAccounts(true);
                }}
              >
                Delete all
              </button>
            </div>
          )}
          <div className={cn.accountsListTableWrapper}>
            <Table
              take="10"
              setPage={(data) => {
                setPage({ ...data, filters });
              }}
              pagination={pagination}
              columns={columns}
              data={formattedData || []}
              onSort={(name) => {
                onSort(
                  name,
                  sort.sortDir === SortDir.DESC ? SortDir.ASC : SortDir.DESC,
                );
              }}
              error={errorsList}
              filters={filters}
              sort={sort}
              tableFormClassName={cn.accoutListTable}
              columnsLabels={columnsLabels}
              onFilterChange={(values) => {
                const nextFilters = { ...values };

                nextFilters.ids = {
                  value: accountsIds as string[],
                  type: SchemaFilterType.STRING,
                };

                onFilter(nextFilters);
              }}
              filtersComponent={filtersComponent}
              handleSubmit={handleSubmit}
              isLoading={isFetching}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default Accounts;
